import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Button, Upload, DatePicker, Slider, Select, Switch,
  Typography, notification, Spin, Tag, Row, Col, Divider, Table, Space, Input, Tabs, Pagination
} from 'antd';
import {
  UploadOutlined, DownloadOutlined, PictureOutlined,
  ReloadOutlined, SaveOutlined, TeamOutlined, CalendarOutlined, SettingOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

const FONT_FAMILIES = [
  // ── Google Fonts (Devanagari) ──────────────────────────
  { label: 'Noto Sans Devanagari',  value: 'Noto Sans Devanagari' },
  { label: 'Noto Serif Devanagari', value: 'Noto Serif Devanagari' },
  { label: 'Hind',                  value: 'Hind' },
  { label: 'Mukta',                 value: 'Mukta' },
  { label: 'Mukta ExtraBold 800',  value: 'Mukta ExtraBold' },
  { label: 'Baloo 2',               value: 'Baloo 2' },
  { label: 'Eczar',                 value: 'Eczar' },
  { label: 'Karma',                 value: 'Karma' },
  { label: 'Biryani',               value: 'Biryani' },
  { label: 'Vesper Libre',          value: 'Vesper Libre' },
  { label: 'Tillana',               value: 'Tillana' },
  { label: 'Rajdhani',              value: 'Rajdhani' },
  { label: 'Tiro Devanagari Hindi', value: 'Tiro Devanagari Hindi' },
  { label: 'Rozha One',             value: 'Rozha One' },
  { label: 'Yatra One',             value: 'Yatra One' },
  { label: 'Kalam',                 value: 'Kalam' },
  { label: 'Laila',                 value: 'Laila' },
  // ── Windows System Fonts (Hindi) ──────────────────────
  { label: 'Kokila (Windows)',       value: 'Kokila' },
  { label: 'Aparajita (Windows)',    value: 'Aparajita' },
  { label: 'Mangal (Windows)',       value: 'Mangal' },
  { label: 'Utsaah (Windows)',       value: 'Utsaah' },
  { label: 'Sanskrit Text (Windows)',value: 'Sanskrit Text' },
  { label: 'Gargi',                  value: 'Gargi' },
  { label: 'Lohit Devanagari',       value: 'Lohit Devanagari' },
  // ── General ───────────────────────────────────────────
  { label: 'Poppins',               value: 'Poppins' },
  { label: 'Arial',                 value: 'Arial' },
  { label: 'Times New Roman',       value: 'Times New Roman' },
  { label: 'Georgia',               value: 'Georgia' },
];

const DEFAULT_LAYOUT = {
  names_top_pct: 40,  names_bottom_pct: 88,
  left_col_pct: 3,    right_col_pct: 52,  col_width_pct: 46,
  font_size: 26,      line2_font_size: 20,
  color: '#6B0000',   line2_color: '#444444',
  font_family: 'Noto Sans Devanagari',
  line2_font_family: 'Noto Sans Devanagari',
  date_top_pct: 34,   date_left_pct: 3,
  count_top_pct: 34,  count_left_pct: 80,
  date_font_size: 22, count_font_size: 28,
  date_color: '#ffffff',  count_color: '#ffffff',
  text_align: 'left',
  line2_text_align: 'center',
  inner_gap: 10,
  entry_gap: 30,
  letter_spacing: 0,
  line2_letter_spacing: 0,
};

export default function DailyPoster() {
  const [tab, setTab]                   = useState('generate'); // 'generate' | 'advance' | 'config'
  const [advanceMonth, setAdvanceMonth] = useState(moment().add(1, 'month'));
  const [advanceMembers, setAdvanceMembers] = useState([]);
  const [loadingAdvance, setLoadingAdvance] = useState(false);
  const [templateImage, setTemplateImage] = useState('');
  const [layout, setLayout]             = useState(DEFAULT_LAYOUT);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [members, setMembers]           = useState([]);
  const [totalPages, setTotalPages]     = useState(0);
  const [currentPage, setCurrentPage]   = useState(0);
  const [previewUrl, setPreviewUrl]     = useState('');
  const [loading, setLoading]           = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [dragging, setDragging]         = useState(null); // { xField, yField }
  const previewContainerRef             = useRef(null);

  // Load config on mount
  useEffect(() => {
    axios.get(`${baseUrl}/daily-poster/config`, AUTH()).then(res => {
      const cfg = res.data.result;
      if (cfg) {
        setTemplateImage(cfg.template_image || '');
        if (cfg.layout_config) setLayout({ ...DEFAULT_LAYOUT, ...cfg.layout_config });
      }
      setConfigLoaded(true);
    }).catch(() => setConfigLoaded(true));
  }, []);

  const updateLayout = (key, val) => setLayout(prev => ({ ...prev, [key]: val }));

  // ── Drag-to-position handlers for config preview markers ──
  const handleMarkerMouseDown = useCallback((e, xField, yField) => {
    e.preventDefault();
    setDragging({ xField, yField });
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging || !previewContainerRef.current) return;
      const rect = previewContainerRef.current.getBoundingClientRect();
      const xPct = Math.round(((e.clientX - rect.left) / rect.width)  * 200) / 2;
      const yPct = Math.round(((e.clientY - rect.top)  / rect.height) * 200) / 2;
      const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      if (dragging.xField) updateLayout(dragging.xField, clamp(xPct, 0, 95));
      if (dragging.yField) updateLayout(dragging.yField, clamp(yPct, 0, 95));
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // Fetch members for selected date
  const fetchMembers = async (date) => {
    setLoadingMembers(true);
    setMembers([]);
    setTotalPages(0);
    setPreviewUrl('');
    try {
      const d = (date || selectedDate).format('YYYY-MM-DD');
      const res = await axios.get(`${baseUrl}/daily-poster/members`, { ...AUTH(), params: { date: d } });
      setMembers(res.data.result || []);
    } catch (e) {
      notification.error({ message: 'Failed to load members.' });
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAdvanceMembers = async (m) => {
    setLoadingAdvance(true);
    setAdvanceMembers([]);
    try {
      const month = (m || advanceMonth).format('YYYY-MM');
      const res = await axios.get(`${baseUrl}/daily-poster/advance-members`, { ...AUTH(), params: { month } });
      setAdvanceMembers(res.data.result || []);
    } catch (e) {
      notification.error({ message: 'Failed to load advance members.' });
    } finally { setLoadingAdvance(false); }
  };

  // Generate poster info (page count) then show first page
  const handleGenerate = async (pageIdx = 0) => {
    if (!templateImage) return notification.warning({ message: 'Please upload a template image first.' });
    if (members.length === 0) return notification.warning({ message: 'No members found for this date.' });
    setLoading(true);
    try {
      const d = selectedDate.format('YYYY-MM-DD');
      // Get page info first
      const info = await axios.post(`${baseUrl}/daily-poster/generate`,
        { date: d, info_only: true }, AUTH());
      const pages = info.data.pages || 1;
      setTotalPages(pages);
      setCurrentPage(pageIdx);
      // Build preview URL (GET with token in header via img src won't work, use POST blob)
      await loadPreviewPage(d, pageIdx);
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Generation failed.' });
    } finally {
      setLoading(false);
    }
  };

  const loadPreviewPage = async (dateStr, pageIdx) => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/daily-poster/generate`,
        { date: dateStr, page_index: pageIdx },
        { ...AUTH(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
      setCurrentPage(pageIdx);
    } catch (e) {
      notification.error({ message: e.response?.data?.message || 'Failed to generate page.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPage = async (pageIdx) => {
    setLoading(true);
    try {
      const d = selectedDate.format('YYYY-MM-DD');
      const res = await axios.post(`${baseUrl}/daily-poster/generate`,
        { date: d, page_index: pageIdx },
        { ...AUTH(), responseType: 'blob' });
      const url  = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href  = url;
      link.download = `daily-poster-${d}-page${pageIdx + 1}.jpg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notification.error({ message: 'Download failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    const d = selectedDate.format('YYYY-MM-DD');
    for (let i = 0; i < totalPages; i++) {
      await handleDownloadPage(i);
      await new Promise(r => setTimeout(r, 400));
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${baseUrl}/daily-poster/save-config`,
        { template_image: templateImage, layout_config: layout }, AUTH());
      notification.success({ message: 'Configuration saved!' });
    } catch (e) {
      notification.error({ message: 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async ({ file }) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${baseUrl}/daily-poster/upload-template`, fd,
        { headers: { ...AUTH().headers, 'Content-Type': 'multipart/form-data' } });
      if (res.data.status) { setTemplateImage(res.data.image_url); notification.success({ message: 'Template uploaded!' }); }
      else notification.error({ message: res.data.message });
    } catch (e) { notification.error({ message: 'Upload failed.' }); }
    finally { setUploading(false); }
  };

  if (!configLoaded) return <div style={{ padding: 40 }}><Spin tip="Loading..." /></div>;

  const memberColumns = [
    { title: '#', dataIndex: 'idx', key: 'idx', width: 40, render: (_, __, i) => i + 1 },
    { title: 'Name', key: 'name', render: r => <span>{r.name} {r.relation} {r.father_name}</span> },
    { title: 'Village / District', key: 'addr', render: r => [r.village, r.district].filter(Boolean).join(', ') },
    { title: '₹', dataIndex: 'amount', key: 'amount' },
  ];

  const SliderRow = ({ label, field, min, max, step = 0.5 }) => (
    <div style={{ marginBottom: 10 }}>
      <Text style={{ fontSize: 11 }}>{label}: <b>{layout[field]}</b></Text>
      <Slider min={min} max={max} step={step} value={layout[field]} onChange={v => updateLayout(field, v)} />
    </div>
  );

  return (
    <div style={{ padding: '16px 20px', background: '#f5f7fa', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Daily Members Poster — आज के भामाशाह</Title>
        <Space>
          {tab !== 'generate' && <Button icon={<CalendarOutlined />} onClick={() => setTab('generate')}>Date Poster</Button>}
          {tab !== 'advance'  && <Button icon={<ClockCircleOutlined />} onClick={() => { setTab('advance'); fetchAdvanceMembers(); }}>Advance Paid</Button>}
          {tab !== 'config'   && <Button icon={<SettingOutlined />} onClick={() => setTab('config')}>Configure</Button>}
          {tab === 'config' && (
            <>
              <Button onClick={() => { setLayout(DEFAULT_LAYOUT); notification.info({ message: 'Positions reset to defaults. Click Save Config to apply.' }); }}>
                Reset Positions
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveConfig}>
                Save Config
              </Button>
            </>
          )}
        </Space>
      </div>

      {tab === 'config' ? (
        /* ═══════════════════ CONFIG TAB ═══════════════════ */
        <Row gutter={16}>
          <Col span={8}>
            {/* Template Upload */}
            <Card size="small" title="Background Template" style={{ marginBottom: 12 }}>
              <Upload showUploadList={false} customRequest={handleUpload} accept="image/*">
                <Button icon={<UploadOutlined />} loading={uploading} block>
                  {templateImage ? 'Replace Template' : 'Upload Template Image'}
                </Button>
              </Upload>
              {templateImage && <img src={templateImage} alt="tmpl" style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />}
            </Card>

            {/* Name colors */}
            <Card size="small" title="Name Style" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 11 }}>Name Color (Line 1)</Text><br />
                <input type="color" value={layout.color} onChange={e => updateLayout('color', e.target.value)}
                  style={{ width: 48, height: 32, border: '1px solid #d9d9d9', borderRadius: 4, padding: 2, cursor: 'pointer' }} />
                <Input size="small" value={layout.color} onChange={e => updateLayout('color', e.target.value)}
                  style={{ width: 90, marginLeft: 8 }} maxLength={7} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 11 }}>Village Color (Line 2)</Text><br />
                <input type="color" value={layout.line2_color} onChange={e => updateLayout('line2_color', e.target.value)}
                  style={{ width: 48, height: 32, border: '1px solid #d9d9d9', borderRadius: 4, padding: 2, cursor: 'pointer' }} />
                <Input size="small" value={layout.line2_color} onChange={e => updateLayout('line2_color', e.target.value)}
                  style={{ width: 90, marginLeft: 8 }} maxLength={7} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 11 }}>Name Line Font</Text>
                <Select value={layout.font_family} onChange={v => updateLayout('font_family', v)} size="small" style={{ width: '100%' }} showSearch filterOption={(input, opt) => opt.children.toLowerCase().includes(input.toLowerCase())}>
                  {FONT_FAMILIES.map(f => (
                    <Option key={f.value} value={f.value}>
                      <span style={{ fontFamily: f.value, fontSize: 13 }}>{f.label}</span>
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 11 }}>Village Line Font</Text>
                <Select value={layout.line2_font_family || layout.font_family} onChange={v => updateLayout('line2_font_family', v)} size="small" style={{ width: '100%' }} showSearch filterOption={(input, opt) => opt.children.toLowerCase().includes(input.toLowerCase())}>
                  {FONT_FAMILIES.map(f => (
                    <Option key={f.value} value={f.value}>
                      <span style={{ fontFamily: f.value, fontSize: 13 }}>{f.label}</span>
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 11 }}>Name Alignment</Text>
                <Select value={layout.text_align || 'left'} onChange={v => updateLayout('text_align', v)} size="small" style={{ width: '100%' }}>
                  <Option value="left">⬅ Left</Option>
                  <Option value="center">↔ Center</Option>
                </Select>
              </div>
              <div>
                <Text style={{ fontSize: 11 }}>Village Line Alignment</Text>
                <Select value={layout.line2_text_align || 'center'} onChange={v => updateLayout('line2_text_align', v)} size="small" style={{ width: '100%' }}>
                  <Option value="left">⬅ Left</Option>
                  <Option value="center">↔ Center</Option>
                </Select>
              </div>
            </Card>
          </Col>

          <Col span={16}>
            <Row gutter={12}>
              {/* Sliders column */}
              <Col span={11}>
                <Card size="small" title="Positions" style={{ marginBottom: 0 }}>
                  <Text strong style={{ fontSize: 11, color: '#1890ff' }}>📋 Names Area</Text>
                  <SliderRow label="Top %"         field="names_top_pct"    min={0}  max={80} />
                  <SliderRow label="Bottom %"      field="names_bottom_pct" min={20} max={100} />
                  <SliderRow label="Left Col %"    field="left_col_pct"     min={0}  max={50} />
                  <SliderRow label="Right Col %"   field="right_col_pct"    min={30} max={90} />
                  <Divider style={{ margin: '6px 0' }} />
                  <Text strong style={{ fontSize: 11, color: '#fa8c16' }}>📅 Date</Text>
                  <SliderRow label="Top %"    field="date_top_pct"   min={0} max={60} />
                  <SliderRow label="Left %"   field="date_left_pct"  min={0} max={50} />
                  <SliderRow label="Font px"  field="date_font_size" min={10} max={60} step={1} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Text style={{ fontSize: 11 }}>Color</Text>
                    <input type="color" value={layout.date_color || '#ffffff'}
                      onChange={e => updateLayout('date_color', e.target.value)}
                      style={{ width: 36, height: 26, border: '1px solid #d9d9d9', borderRadius: 3, cursor: 'pointer', padding: 1 }} />
                    <Input size="small" value={layout.date_color || '#ffffff'}
                      onChange={e => updateLayout('date_color', e.target.value)}
                      style={{ width: 80 }} maxLength={7} />
                  </div>
                  <Divider style={{ margin: '6px 0' }} />
                  <Text strong style={{ fontSize: 11, color: '#52c41a' }}>🔢 Count</Text>
                  <SliderRow label="Top %"   field="count_top_pct"   min={0}  max={60} />
                  <SliderRow label="Left %"  field="count_left_pct"  min={30} max={95} />
                  <SliderRow label="Font px" field="count_font_size" min={10} max={80} step={1} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Text style={{ fontSize: 11 }}>Color</Text>
                    <input type="color" value={layout.count_color || '#ffffff'}
                      onChange={e => updateLayout('count_color', e.target.value)}
                      style={{ width: 36, height: 26, border: '1px solid #d9d9d9', borderRadius: 3, cursor: 'pointer', padding: 1 }} />
                    <Input size="small" value={layout.count_color || '#ffffff'}
                      onChange={e => updateLayout('count_color', e.target.value)}
                      style={{ width: 80 }} maxLength={7} />
                  </div>
                  <Divider style={{ margin: '6px 0' }} />
                  <Text strong style={{ fontSize: 11, color: '#722ed1' }}>🔤 Font & Spacing</Text>
                  <SliderRow label="Name size px"       field="font_size"       min={10} max={60} step={1} />
                  <SliderRow label="Village size px"    field="line2_font_size" min={8}  max={40} step={1} />
                  <SliderRow label="Name→Village gap px"    field="inner_gap"           min={2}   max={60}  step={1} />
                  <SliderRow label="Village→Next gap px"   field="entry_gap"           min={5}   max={100} step={1} />
                  <SliderRow label="Name letter spacing px" field="letter_spacing"      min={-2}  max={20}  step={0.5} />
                  <SliderRow label="Village letter spacing" field="line2_letter_spacing" min={-2} max={20}  step={0.5} />
                </Card>
              </Col>
              {/* Live preview column */}
              <Col span={13}>
                <Card size="small" title={<span>Live Preview <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>— drag 📅 DATE and 🔢 COUNT to reposition</span></span>}>
                  {!templateImage ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>
                      <PictureOutlined style={{ fontSize: 32 }} /><br />Upload template first
                    </div>
                  ) : (
                    <div
                      ref={previewContainerRef}
                      style={{ position: 'relative', display: 'inline-block', width: '100%', userSelect: 'none', cursor: dragging ? 'grabbing' : 'default' }}
                    >
                      <img src={templateImage} alt="preview" style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />

                      {/* Names area box */}
                      <div style={{
                        position: 'absolute',
                        top: `${layout.names_top_pct}%`,
                        left: `${layout.left_col_pct}%`,
                        width: `${layout.col_width_pct * 2 + (layout.right_col_pct - layout.left_col_pct - layout.col_width_pct)}%`,
                        height: `${layout.names_bottom_pct - layout.names_top_pct}%`,
                        border: '2px dashed #1890ff',
                        background: 'rgba(24,144,255,0.06)',
                        pointerEvents: 'none',
                      }}>
                        <span style={{ position: 'absolute', top: 2, left: 4, fontSize: 9, background: '#1890ff', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>NAMES AREA</span>
                      </div>

                      {/* Left col — flow layout so inner_gap and entry_gap work independently */}
                      {(() => {
                        const sc = 0.42;
                        const nameStyle = { fontFamily: layout.font_family, fontSize: `${layout.font_size * sc}px`, color: layout.color, fontWeight: layout.font_family === 'Mukta ExtraBold' ? 800 : 700, lineHeight: 1.2, textAlign: layout.text_align || 'left', letterSpacing: `${(layout.letter_spacing || 0) * sc}px` };
                        const villageStyle = { fontFamily: layout.line2_font_family || layout.font_family, fontSize: `${layout.line2_font_size * sc}px`, color: layout.line2_color, lineHeight: 1.2, textAlign: layout.line2_text_align || layout.text_align || 'center', letterSpacing: `${(layout.line2_letter_spacing || 0) * sc}px`, marginTop: `${layout.inner_gap * sc}px` };
                        return (
                          <div style={{ position: 'absolute', top: `${layout.names_top_pct}%`, left: `${layout.left_col_pct}%`, width: `${layout.col_width_pct}%`, overflow: 'hidden', maxHeight: `${layout.names_bottom_pct - layout.names_top_pct}%`, pointerEvents: 'none' }}>
                            {['महेंद्रकुमारजी पुत्र श्री नरसाजी', 'इन्द्रसिंह जी पुत्र श्री भवरसिंह जी', 'भवानीसिंह जी पुत्र श्री लालसिंह जी'].map((name, i) => (
                              <div key={i} style={{ paddingBottom: `${layout.entry_gap * sc}px` }}>
                                <div style={nameStyle}>{name}</div>
                                <div style={villageStyle}>जूंजाणी, जालोर</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Right col — same flow layout */}
                      {(() => {
                        const sc = 0.42;
                        const nameStyle = { fontFamily: layout.font_family, fontSize: `${layout.font_size * sc}px`, color: layout.color, fontWeight: layout.font_family === 'Mukta ExtraBold' ? 800 : 700, lineHeight: 1.2, textAlign: layout.text_align || 'left', letterSpacing: `${(layout.letter_spacing || 0) * sc}px` };
                        const villageStyle = { fontFamily: layout.line2_font_family || layout.font_family, fontSize: `${layout.line2_font_size * sc}px`, color: layout.line2_color, lineHeight: 1.2, textAlign: layout.line2_text_align || layout.text_align || 'center', letterSpacing: `${(layout.line2_letter_spacing || 0) * sc}px`, marginTop: `${layout.inner_gap * sc}px` };
                        return (
                          <div style={{ position: 'absolute', top: `${layout.names_top_pct}%`, left: `${layout.right_col_pct}%`, width: `${layout.col_width_pct}%`, overflow: 'hidden', maxHeight: `${layout.names_bottom_pct - layout.names_top_pct}%`, pointerEvents: 'none' }}>
                            {['ब्रह्मर्षि खेतेबर वेद विज्ञान गुरुकुल', 'नरपतसिंह जी पुत्र श्री मंगलसिंह जी', 'श्रवणसिंहजी पुत्र श्री कानसिंहजी'].map((name, i) => (
                              <div key={i} style={{ paddingBottom: `${layout.entry_gap * sc}px` }}>
                                <div style={nameStyle}>{name}</div>
                                <div style={villageStyle}>बिजरोल खेड़ा, जालोर</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Date marker — draggable */}
                      <div
                        onMouseDown={e => handleMarkerMouseDown(e, 'date_left_pct', 'date_top_pct')}
                        style={{
                          position: 'absolute', top: `${layout.date_top_pct}%`, left: `${layout.date_left_pct}%`,
                          background: layout.date_color || '#ffffff',
                          color: '#333', border: '2px solid #fa8c16',
                          fontSize: `${layout.date_font_size * 0.42}px`,
                          padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap',
                          fontWeight: 700, cursor: 'grab', fontFamily: layout.font_family,
                          boxShadow: dragging?.yField === 'date_top_pct' ? '0 0 0 3px rgba(250,140,22,0.4)' : 'none',
                        }}>
                        📅 14 जून 2026
                      </div>

                      {/* Count marker — draggable */}
                      <div
                        onMouseDown={e => handleMarkerMouseDown(e, 'count_left_pct', 'count_top_pct')}
                        style={{
                          position: 'absolute', top: `${layout.count_top_pct}%`, left: `${layout.count_left_pct}%`,
                          background: layout.count_color || '#ffffff',
                          color: '#333', border: '2px solid #52c41a',
                          fontSize: `${layout.count_font_size * 0.42}px`,
                          padding: '2px 6px', borderRadius: 3, whiteSpace: 'nowrap',
                          fontWeight: 700, cursor: 'grab',
                          boxShadow: dragging?.yField === 'count_top_pct' ? '0 0 0 3px rgba(82,196,26,0.4)' : 'none',
                        }}>
                        🔢 42
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

      ) : (
        /* ═══════════════════ GENERATE TAB ═══════════════════ */
        <>
        {!templateImage && (
          <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
            padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <Text strong>First time setup required</Text><br />
              <Text type="secondary">
                Click <b>Configure Template</b> (top right) → Upload your poster background image → Adjust the position sliders to match your design → Save Config. Then come back here to generate.
              </Text>
            </div>
            <Button type="primary" size="small" onClick={() => setTab('config')} style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
              Configure Now
            </Button>
          </div>
        )}
        {templateImage && (
          <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
            padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <Text type="secondary">
              If names appear in wrong positions, click <b>Configure Template</b> to adjust <b>Names Area Top %</b>, <b>Date %</b>, and <b>Count %</b> sliders to match your poster design.
            </Text>
          </div>
        )}
        <Row gutter={16}>
          {/* Left: date picker + member list */}
          <Col span={9}>
            <Card size="small" title={<span><CalendarOutlined /> Select Date</span>} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <DatePicker
                  value={selectedDate}
                  onChange={d => { setSelectedDate(d); setMembers([]); setPreviewUrl(''); setTotalPages(0); }}
                  style={{ flex: 1 }}
                  format="DD MMM YYYY"
                />
                <Button type="primary" loading={loadingMembers}
                  onClick={() => fetchMembers(selectedDate)}>
                  Load Members
                </Button>
              </div>
            </Card>

            {selectedDate && (
              <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6,
                padding: '8px 12px', marginBottom: 8, fontSize: 12 }}>
                <ClockCircleOutlined style={{ color: '#2f54eb', marginRight: 6 }} />
                <Text>Members who paid on <Text strong>{selectedDate.format('D MMM YYYY')}</Text> + advance-paid members whose <Text strong>joining date</Text> falls on any <Text strong>{selectedDate.date()}th</Text> and whose coverage includes <Text strong>{selectedDate.format('MMMM YYYY')}</Text> are shown.</Text>
              </div>
            )}
            {members.length > 0 && (
              <Card size="small"
                title={<span><TeamOutlined /> Members — <Tag color="blue">{members.length} total</Tag></span>}
                style={{ marginBottom: 12 }}>
                <Table
                  dataSource={members}
                  columns={memberColumns}
                  rowKey="payment_id"
                  size="small"
                  pagination={{ pageSize: 10, size: 'small' }}
                  scroll={{ y: 300 }}
                />
                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  <Button type="primary" icon={<PictureOutlined />} block
                    loading={loading} onClick={() => handleGenerate(0)}>
                    Generate Poster
                  </Button>
                </div>
              </Card>
            )}
          </Col>

          {/* Right: poster preview + download */}
          <Col span={15}>
            <Card size="small"
              title={
                <Space>
                  <span>Poster Preview</span>
                  {totalPages > 1 && <Tag color="orange">{totalPages} pages</Tag>}
                </Space>
              }
              extra={previewUrl && (
                <Space size={6}>
                  <Button icon={<DownloadOutlined />} size="small"
                    onClick={() => handleDownloadPage(currentPage)}>
                    Download Page {currentPage + 1}
                  </Button>
                  {totalPages > 1 && (
                    <Button icon={<DownloadOutlined />} type="primary" size="small"
                      onClick={handleDownloadAll}>
                      All ({totalPages})
                    </Button>
                  )}
                </Space>
              )}>

              {/* ── Page navigation — scrollable, never overflows ── */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px 0 12px',
                  borderBottom: '1px solid #f0f0f0',
                  marginBottom: 12,
                }}>
                  <Pagination
                    current={currentPage + 1}
                    total={totalPages}
                    pageSize={1}
                    size="small"
                    showSizeChanger={false}
                    showQuickJumper={totalPages > 10}
                    itemRender={(page, type, originalEl) => {
                      if (type === 'page') {
                        return (
                          <button
                            onClick={() => loadPreviewPage(selectedDate.format('YYYY-MM-DD'), page - 1)}
                            style={{
                              border: 'none', background: 'transparent',
                              cursor: 'pointer', padding: 0,
                            }}
                          >
                            {page}
                          </button>
                        );
                      }
                      return originalEl;
                    }}
                    onChange={(page) => loadPreviewPage(selectedDate.format('YYYY-MM-DD'), page - 1)}
                  />
                </div>
              )}

              {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="Generating poster..." /></div>}
              {!loading && !previewUrl && (
                <div style={{ textAlign: 'center', padding: 60, color: '#bbb' }}>
                  <PictureOutlined style={{ fontSize: 48 }} />
                  <div style={{ marginTop: 12 }}>Select a date, load members, then click Generate Poster</div>
                </div>
              )}
              {!loading && previewUrl && (
                <img src={previewUrl} alt="poster preview" style={{ width: '100%', borderRadius: 4 }} />
              )}
            </Card>
          </Col>
        </Row>
        </>
      )}

      {tab === 'advance' && (
        /* ═══════════════════ ADVANCE PAID TAB ═══════════════════ */
        <div>
          <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8,
            padding: '10px 16px', marginBottom: 16 }}>
            <Text><ClockCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
              Shows members who have already paid and their coverage extends into the selected month or beyond.
              Member paid on 14th with advance → appears on the 14th date poster too.
            </Text>
          </div>
          <Card size="small" title={<span><ClockCircleOutlined /> Select Month to Check</span>}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <DatePicker.MonthPicker
                value={advanceMonth}
                onChange={m => { setAdvanceMonth(m); setAdvanceMembers([]); }}
                format="MMM YYYY"
                style={{ flex: 1 }}
              />
              <Button type="primary" loading={loadingAdvance}
                onClick={() => fetchAdvanceMembers(advanceMonth)}>
                Load Members
              </Button>
            </div>
            {advanceMembers.length > 0 && (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <Tag color="blue">{advanceMembers.length} members paid in advance</Tag>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    for {advanceMonth.format('MMMM YYYY')}
                  </Text>
                </div>
                <Table
                  dataSource={advanceMembers}
                  rowKey="payment_id"
                  size="small"
                  pagination={{ pageSize: 20, size: 'small' }}
                  scroll={{ y: 500 }}
                  columns={[
                    { title: '#', width: 40, render: (_, __, i) => i + 1 },
                    { title: 'Name', key: 'name', render: r => <span style={{ fontWeight: 600 }}>{r.name} {r.relation} {r.father_name}</span> },
                    { title: 'Village / District', key: 'addr', render: r => [r.village, r.district].filter(Boolean).join(', ') },
                    { title: 'Paid Till', dataIndex: 'end_month', key: 'end_month',
                      render: v => v ? moment(v).format('MMM YYYY') : '—' },
                    { title: '₹ Total', dataIndex: 'total_amount', key: 'amt',
                      render: v => `₹${Number(v).toLocaleString('en-IN')}` },
                  ]}
                />
              </div>
            )}
            {!loadingAdvance && advanceMembers.length === 0 && (
              <Text type="secondary">Click "Load Members" to see advance-paid members for the selected month.</Text>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
